import { Link } from "react-router-dom";
import { format } from "date-fns";
import type { AttemptHistoryItem } from "@/types";

interface AttemptHistoryProps {
  attempts: AttemptHistoryItem[];
  showExamTitle?: boolean;
}

export default function AttemptHistory({ attempts, showExamTitle = true }: AttemptHistoryProps) {
  if (attempts.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <p className="mt-3 text-sm text-gray-500">No attempts yet</p>
        <div className="mt-4 flex justify-center gap-4">
          <Link to="/exams" className="text-sm font-medium text-primary-600 hover:text-primary-500">
            Take a mock exam
          </Link>
          <Link to="/categories" className="text-sm font-medium text-primary-600 hover:text-primary-500">
            Practice by category
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              {showExamTitle && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Score
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Result
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Time
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {attempts.map((attempt) => {
              const timeTaken = attempt.time_taken_seconds ?? 0;
              const minutes = Math.floor(timeTaken / 60);
              const seconds = timeTaken % 60;
              const isCategory = !!attempt.category_id;
              const title = isCategory ? attempt.category_name : attempt.exam_title;

              return (
                <tr key={attempt.id} className="hover:bg-gray-50 transition-colors">
                  {showExamTitle && (
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {title ?? "—"}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        isCategory
                          ? "bg-primary-50 text-primary-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {isCategory ? "Category" : "Mock Exam"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {format(new Date(attempt.started_at), "dd MMM yyyy, HH:mm")}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                    {attempt.score !== null ? (
                      `${attempt.score}/${attempt.total_questions}`
                    ) : (
                      <span className="text-gray-400">In progress</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {attempt.passed !== null ? (
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${
                          attempt.passed
                            ? "bg-success-100 text-success-800"
                            : "bg-danger-100 text-danger-800"
                        }`}
                      >
                        {attempt.passed ? "PASS" : "FAIL"}
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold bg-warning-100 text-warning-800">
                        INCOMPLETE
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {attempt.completed_at ? `${minutes}m ${seconds}s` : "-"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!isCategory && attempt.completed_at ? (
                      <Link
                        to={`/attempts/${attempt.id}/review`}
                        className="text-sm font-medium text-primary-600 hover:text-primary-500"
                      >
                        Review
                      </Link>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
